/**
 * cellSpriteMatcher — variant-metadata module (slimmed, scan overhaul v2).
 *
 * HISTORICAL NOTE. This module used to be the per-cell sprite CHROMA MATCHER: a
 * hue-histogram cosine-similarity classifier that compared each cell's sprite
 * ROI against a pre-computed PokeAPI colour fingerprint (SPRITE_FINGERPRINTS)
 * and returned a caught/missing/uncertain verdict. It was a pre-Vision
 * (Tesseract-era) heuristic — the number OCR was unreliable, so a colour-based
 * "does this cell look like species X is caught?" signal was layered on top.
 *
 * Now Vision reads the dex NUMBER at ~100% confidence, so the species is known
 * and the fingerprint match is over-engineered — it produced confident
 * `chroma-match` false-positives on PiP-blended cells (device log 2026-07-07).
 * The RUNTIME caught/silhouette/empty decision moved to the pure
 * presence-density classifier in `cellPresence.ts`. The 500+ lines of
 * fingerprint / histogram / cosine-similarity logic (matchCellToSprite,
 * computeExpectedSpriteSim, computeHueHistogram, getReferenceFingerprint, the
 * SPRITE_FINGERPRINTS lookup, etc.) have been DELETED from this file.
 *
 * The generated fingerprint DATA (spriteFingerprints.generated.json) and its
 * core builder (spriteFingerprintCore.ts) are intentionally KEPT in the repo as
 * data for a possible future Phase 5 optional confidence overlay, but they are
 * no longer imported by the runtime path.
 *
 * What survives here is the VARIANT-PROPERTY metadata that is still useful
 * everywhere: which variants are BINARY (sprite-or-nothing), which render the
 * SHINY sprite recolour, and which use the local-cell-background presence gate.
 * These are cheap set-membership lookups with no pixel processing.
 */

import type { PokedexVariant } from "@/lib/pogoPokedexVariantClassifier";

/**
 * Variants whose Pokedex cells are BINARY: either a full-colour sprite is
 * drawn (caught) or the cell is empty (not caught). There is no tinted
 * silhouette "seen-only" state for these dexes.
 *
 * Confirmed against on-device screenshots:
 *   - lucky, perfect, xxs, xxl: sprite-or-nothing.
 *   - shiny: three-state (silhouettes for shiny-seen exist).
 *   - pokemon (default), purified, shadow, mega: three-state.
 *
 * gmax is also three-state but ships in a 3-column grid; the grid-width
 * override lives in dexOcrRoi.ts, not here.
 */
export const BINARY_VARIANTS: ReadonlySet<PokedexVariant> = new Set<PokedexVariant>([
  "lucky",
  "perfect",
  "xxs",
  "xxl",
]);

/** True when the named variant uses the binary sprite-or-nothing cell. */
export function isBinaryVariant(variant: PokedexVariant): boolean {
  return BINARY_VARIANTS.has(variant);
}

/**
 * The SINGLE source of truth for "which dex variants render their cells with
 * the SHINY recolour of the species sprite". Membership is exactly the
 * shiny-family dexes:
 *   - shiny        — the Shiny dex itself.
 *   - shundo       — Shiny + 100% IV (manual-only dex; still a shiny sprite).
 *   - shinyLucky   — Shiny + Lucky (manual-only dex; still a shiny sprite).
 *
 * NOTHING else belongs here. A non-shiny variant (pokemon/lucky/perfect/xxl/
 * xxs/purified/shadow/mega/gmax) renders the base-colour sprite. Kept as
 * variant metadata even though the fingerprint match that consumed it is gone,
 * because a future Phase 5 overlay would need the same shiny-vs-base routing.
 */
export const SHINY_FAMILY_VARIANTS: ReadonlySet<PokedexVariant> = new Set<PokedexVariant>([
  "shiny",
  "shundo",
  "shinyLucky",
]);

/** True only for the shiny-family dexes whose cells render the shiny sprite. */
export function isShinyFamilyVariant(variant: PokedexVariant): boolean {
  return SHINY_FAMILY_VARIANTS.has(variant);
}

/**
 * Variants whose caught state is decided by SPRITE PRESENCE relative to the
 * cell's own background rather than by a positive aura marker or the global
 * pink-relative chroma gate. This is the binary variants PLUS mega (PR-E18b:
 * caught Megas render as plain full-colour sprites on the pink deep-dex page
 * with no aura, and a faint Mega sprite is indistinguishable from the pink page
 * background by chroma alone, so only the local-cell-background presence path
 * can catch it). Mega is NOT in BINARY_VARIANTS because it is three-state
 * elsewhere (a tinted silhouette "seen" cell exists); it only shares the
 * presence GATE, not the binary cell semantics.
 */
const PRESENCE_VARIANTS: ReadonlySet<PokedexVariant> = (() => {
  const s = new Set<PokedexVariant>(BINARY_VARIANTS);
  s.add("mega");
  return s;
})();

/**
 * True when the variant's presence is decided by the local-cell-background
 * gate (binary variants plus mega) rather than the global-chroma gate.
 */
export function usesPresenceGate(variant: PokedexVariant): boolean {
  return PRESENCE_VARIANTS.has(variant);
}
